// OperationStatus (N3/N4) -> clave de icono + clave i18n (RN35): el estado se dice SIEMPRE con
// icono y texto traducido, nunca solo con color. Módulo puro — `OperationsBlock.jsx` traduce el
// `iconKey` a un componente de MUI; aquí no hay JSX (mismo corte que `partTrackStepState.js`).
//
// ═══ ADDENDUM 33 (E39) / S449 — EL VOCABULARIO PASA A SER EL DE MOS ═══
//
// Ocho valores, idénticos a los que reporta la pantalla de MOS. El PO eligió que las dos pantallas
// respondan a la MISMA pregunta sobre el mismo job, porque respondían a dos distintas.
//
// ⚠️ ESTO REVIERTE DOS DECISIONES DE ESTA MISMA SEMANA, y conviene que se lea aquí:
//   · `PLANNED` / `NOT_PLANNED` DESAPARECEN. Se inferían de si existía una tarea planificada con
//     fecha — 2.814 de 2.824 filas salían de esa inferencia—. Ahora el estado ausente no se
//     interpreta: Sun responde `PENDING` (alguien puede trabajarla ya) o `NOT_AVAILABLE` (la
//     secuencia aún no lo permite), que es otra pregunta y sí tiene respuesta en el dato.
//   · `IN_PROGRESS` SE PARTE. El addendum 24 ya había sacado `INTERRUPTED`; ahora se separan
//     además `ACTIVE_EXECUTION` y `ACTIVE_PREPARATION`, que el vocabulario anterior fundía porque
//     leía el TIPO de estado y el tipo los colapsa en `ACTIVE`.
//
// ⚠️ `NOT_AVAILABLE` NO ES EL `'NOT AVAILABLE'` DE MOS, y la diferencia es UN CARÁCTER. La pantalla
// de MOS recibe el literal CON ESPACIO por el campo del tipo, y Jupiter ya tiene trato para él en
// tres sitios (`JobStatusChip`, `canStartJob`, `STATUS_ACTIONS`). Aquí llega CON GUION BAJO y por
// el campo `status`. No se reutiliza ni uno de esos tres: comparan el literal con espacio, así que
// tocarlos rompería la otra pantalla y reutilizarlos no funcionaría aquí. Es la misma razón por la
// que `partTrackStepState.js` vive aparte — dos enums que se parecen no son el mismo enum.
//
// ⚠️ Story 204 (RN1.5): the i18n key of a status is its own CODE, translated by SystemValueChip. Only the
// seven codes whose text still differs from the same code elsewhere keep a key of their own (P1-bis, PO
// decision 2026-10-13): `Finished`, `Cancelled operation`, `Interrupted operation`, `Executing operation`,
// `Preparing operation`, `Waiting operation` and `Pending operation` — e.g. `CANCELLED` is also a part's
// lifecycle group and `PENDING` a shared key with another text. `NOT_AVAILABLE` goes by its code, which
// carries the text `Unavailable operation` had.
//
// 📌 LO QUE VAS A VER EN PANTALLA, medido sobre las 212 operaciones que lee el endpoint:
//   · ~109 caen en `NOT_AVAILABLE` — LA MITAD de la tabla, no un caso raro. Por eso es de los
//     neutros: si la mitad de la pantalla pidiera atención, ninguna fila la pediría.
//   · `ACTIVE_PREPARATION` tiene CERO filas. Su icono y su color NO se han podido ver funcionar y
//     quedan DECLARADOS, no dados por buenos.
const OPERATION_STATUS_VIEWS = {
  FINISHED: { textKey: 'Finished', iconKey: 'finished' },
  CANCELLED: { textKey: 'Cancelled operation', iconKey: 'cancelled' },
  INTERRUPTED: { textKey: 'Interrupted operation', iconKey: 'interrupted' },
  ACTIVE_EXECUTION: { textKey: 'Executing operation', iconKey: 'active-execution' },
  ACTIVE_PREPARATION: { textKey: 'Preparing operation', iconKey: 'active-preparation' },
  WAITING: { textKey: 'Waiting operation', iconKey: 'waiting' },
  PENDING: { textKey: 'Pending operation', iconKey: 'pending' },
  NOT_AVAILABLE: { textKey: 'NOT_AVAILABLE', iconKey: 'not-available' }
}

// Story 204 (RN1.5, P1-bis): the codes that keep a key of their own because their text differs from the same code
// elsewhere; every other code is asked by itself.
export const OPERATION_STATUS_LEGACY_KEYS = Object.freeze(Object.fromEntries(
  Object.entries(OPERATION_STATUS_VIEWS).filter(([status, view]) => view.textKey !== status).map(([status, view]) => [status, view.textKey])))

/**
 * Los ocho valores, en orden de ciclo de vida.
 *
 * ⚠ NACIÓ para que la leyenda del bloque de Operaciones no pudiera quedarse corta, y esa leyenda YA
 * NO EXISTE: el PO la retiró entera tras verla funcionando como filtro. Lo que sigue guardando es
 * el MAPA DE ICONOS de `OperationsBlock`: las filas siguen pintando los ocho estados, y el test que
 * compara los ocho trazos SVG usa esta lista para saber cuáles son. Si algún día ese test se va,
 * esto se va con él — hoy no es un export solo de test, es el contrato entre los dos mapas.
 *
 * ⚠ Su compañera `operationStatusTextKeyForIcon` SÍ se retiró con la leyenda: era su único
 * consumidor de producción y quedarse habría sido código muerto con un test que lo tapa.
 */
export const ALL_OPERATION_STATUS_ICON_KEYS = Object.values(OPERATION_STATUS_VIEWS).map((view) => view.iconKey)

/**
 * El literal que llega POR EL CABLE -> el nombre del enum. (S380, decisión del PO)
 *
 * ⚠️ SON DOS LITERALES PARA EL MISMO VALOR, y viajan en DOS CAMPOS DISTINTOS de la misma respuesta:
 *   · `status` lo emite `OperationStatus.toDtoValue()`, que devuelve **"NOT AVAILABLE" CON ESPACIO**
 *     para ese único valor —paridad con lo que la pantalla de MOS enseña, que es lo que el PO
 *     quiere— y el nombre tal cual para los otros siete.
 *   · `planningGroupNotEditableReason` lo emitía `status.name()`, siempre con guion bajo. Desde S461
 *     (el grupo se edita en cualquier estado) Jupiter ya no lo lee, y Sun lo retira.
 *
 * Se traduce AQUÍ, en la frontera, y no en el mapa: así dentro de Jupiter hay UN vocabulario y no
 * dos, y el filtro, la leyenda y la píldora hablan todos el mismo. Poner la
 * doble clave en el mapa habría repartido la rareza del cable por todo lo que lo consulta.
 *
 * ⚠️ Y sigue sin tocarse el trato de la pantalla de MOS para `'NOT AVAILABLE'` (`JobStatusChip`,
 * `canStartJob`, `STATUS_ACTIONS`). Que ahora el literal coincida NO los hace reutilizables: son de
 * otra pantalla, con otras reglas, y compartir una cadena no es compartir un significado.
 */
export const operationStatusFromWire = (value) => (value === 'NOT AVAILABLE' ? 'NOT_AVAILABLE' : value)

/**
 * ⚠ Un valor desconocido devuelve claves VACÍAS y el llamante pinta la píldora sin icono ni texto.
 * No es lo deseable, pero es deliberado frente a inventarle un estado: Sun falla la lectura entera
 * ante un código que no reconoce (`OperationStatusPolicy.evaluateOrEmpty`), así que un valor que
 * llegue aquí sin estar en el mapa solo puede significar que el servidor no es el que este cliente
 * espera — el mismo diagnóstico de S341, y se ve antes callando que inventando.
 */
export const operationStatusView = (status) => OPERATION_STATUS_VIEWS[status] || { textKey: '', iconKey: '' }
